import React from 'react';
import {
  Form,
  useActionData,
  useLocation,
  useNavigate,
  useNavigation,
  useSubmit
} from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Textarea } from '@oysterjs/ui/textarea';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { ValidationError } from '@oysterjs/core/graphql';

import {
  Action,
  DocumentPagesSource,
  Resource,
  type CreateHtmlDocumentTemplateMutation,
  HtmlDocumentTemplateFieldsFragment,
  HtmlDocumentTemplateInput,
  HtmlDocumentTemplateStyleInput,
  TemplateDesignSource
} from '~/api/graphql';
import { useUpdateHtmlDocumentTemplateMutation } from '~/api/graphql';
import { LEGACY_DESIGN_CHOICE } from '~/api/data/html-templates/mutations';
import { editorSaveContext } from '~/components/document-editor/use-editor-document-persistence';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { CREATE_CARA_WORKSPACE, PermissionNotice, usePermissions } from '~/components/permissions';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { visibilityInputFromValue } from '~/components/visibility/visibility-selector.utils';

import {
  appendHeaderFooterToFormData,
  headerFooterHasImageSlot
} from '~/components/library/lib/header-footer';
import { useHtmlEditorFormState } from '~/components/library/hooks/use-html-editor-form-state';
import { useHtmlEditorPreview } from '~/components/library/hooks/use-html-editor-preview';
import { htmlDocumentTemplateHasBody } from '~/components/workspaces/lib/html-document-artifact';
import {
  usePrefixSuffixFiles,
  type UsePrefixSuffixFilesResult
} from '~/components/library/hooks/use-prefix-suffix-files';

import {
  HtmlEditorDesignFields,
  startsWithoutDesign,
  templateDesignChoice
} from './html-editor-design-fields';
import { HtmlEditorHeaderFooterPanel } from './html-editor-header-footer-panel';
import { HtmlEditorPreviewPane } from './html-editor-preview-pane';
import { HtmlEditorPrefixSuffixFiles } from './html-editor-prefix-suffix-files';
import { HtmlEditorStylePanel } from './html-editor-style-panel';
import { UseTemplateInWorkspaceDialog } from './use-template-in-workspace-dialog';

export type HtmlEditorKind = 'style' | 'template';

const CREATE_HTML_TEMPLATE = [{ resource: Resource.HtmlDocumentTemplate, action: Action.Create }];
const UPDATE_HTML_TEMPLATE = [{ resource: Resource.HtmlDocumentTemplate, action: Action.Update }];

/**
 * Shared slideout for creating and editing HTML document templates. The
 * `kind` prop distinguishes the two list contexts the editor is used from:
 *
 *  - `style`: pure styles (no body template). Used for Create + Edit.
 *  - `template`: templates with a body. The body itself is edited via the
 *    inline TipTap editor in the preview pane (only in edit mode); the form
 *    on the left manages everything else.
 *
 * Word templates use a separate dedicated editor — see word-template-editor.
 */
export function HtmlEditorPage(props: {
  kind: HtmlEditorKind;
  editMode: boolean;
  initialForm: HtmlDocumentTemplateInput;
  initialTemplate?: HtmlDocumentTemplateFieldsFragment;
  validationErrors: ValidationError[];
  /**
   * URL of the list page to navigate back to when the user closes the
   * slideout. For example `/app/library/styles` or `/app/library/templates`.
   */
  listUrl: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateHtmlDocumentTemplateMutation>;

  const formState = useHtmlEditorFormState({
    initialForm: props.initialForm,
    initialTemplate: props.initialTemplate,
    initialValidationErrors: props.validationErrors
  });
  const preview = useHtmlEditorPreview();

  const prefix = usePrefixSuffixFiles({});
  const suffix = usePrefixSuffixFiles({});

  const visibilityEntitled = useVisibilityEntitlement();
  const editingOwnerCheck = useVisibilityEditAllowed(props.initialTemplate?.createdBy?.id ?? null);
  const visibilityEnabled = props.editMode ? editingOwnerCheck : visibilityEntitled;

  const hasHeaderFooterImageSlots = React.useMemo(
    () => headerFooterHasImageSlot(formState.headerFooter),
    [formState.headerFooter]
  );

  const [updateHtmlDocumentTemplateMutation] = useUpdateHtmlDocumentTemplateMutation();
  const isTemplate = props.kind === 'template';
  // Designs replace the Style-era controls, but a style has no design to choose. The server
  // refuses design fields unless v4 is on as well as the brand gate.
  const v4On = useFlag(FEATURE_FLAGS.DOCUMENT_GENERATION_V4);
  const brandOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const designsOn = v4On && brandOn && isTemplate;
  const [designId, setDesignId] = React.useState<string | null>(() =>
    templateDesignChoice(props.initialTemplate)
  );
  const offersLegacy = props.initialTemplate?.designSource === TemplateDesignSource.Legacy;
  // A save that leaves Legacy takes the option away when it lands. Legacy picked again while it
  // was out would otherwise stay chosen with no option to show it, and the next save would send a
  // choice the server refuses, so the picker falls back to what was saved.
  if (designId === LEGACY_DESIGN_CHOICE && !offersLegacy) {
    setDesignId(templateDesignChoice(props.initialTemplate));
  }
  const [startPages, setStartPages] = React.useState(
    props.initialTemplate?.startPages ?? DocumentPagesSource.Design
  );
  const [printsOwnHeaderFooter, setPrintsOwnHeaderFooter] = React.useState(
    props.initialTemplate?.printsOwnHeaderFooter ?? false
  );
  const [endPages, setEndPages] = React.useState(
    props.initialTemplate?.endPages ?? DocumentPagesSource.Design
  );
  const withoutDesign = startsWithoutDesign(designId);

  // Leaving the template's PDF throws away anything staged for that end; the stored file stays,
  // so switching back finds it.
  const changePages = (
    set: (value: DocumentPagesSource) => void,
    files: UsePrefixSuffixFilesResult,
    value: DocumentPagesSource
  ) => {
    if (value !== DocumentPagesSource.Template) files.discardPending();
    set(value);
  };

  const templateFileMissing = (
    pages: DocumentPagesSource,
    files: UsePrefixSuffixFilesResult,
    stored: { id: string }[] | undefined
  ) =>
    designsOn &&
    !withoutDesign &&
    pages === DocumentPagesSource.Template &&
    files.files.length === 0 &&
    (!stored?.length || files.reset);
  const startFileMissing = templateFileMissing(
    startPages,
    prefix,
    props.initialTemplate?.prefixFiles
  );
  const endFileMissing = templateFileMissing(endPages, suffix, props.initialTemplate?.suffixFiles);
  const missingTemplateFile = startFileMissing || endFileMissing;

  // Reachable by URL on both routes, so the slideout checks for itself rather
  // than trusting the list page's buttons to have been visible.
  const permissions = usePermissions();
  const canEdit = permissions.hasAllPermissions(
    props.editMode ? UPDATE_HTML_TEMPLATE : CREATE_HTML_TEMPLATE
  );
  // The workspace reads the saved template by id, so only a stored template with a body can be used.
  const usableTemplate =
    isTemplate && props.initialTemplate && htmlDocumentTemplateHasBody(props.initialTemplate)
      ? props.initialTemplate
      : null;
  const canUseInWorkspace =
    !!usableTemplate && permissions.hasAllPermissions(CREATE_CARA_WORKSPACE);
  const [useInWorkspaceOpen, setUseInWorkspaceOpen] = React.useState(false);

  // Re-render the iframe preview once the navigation action settles (i.e.
  // the API write completed and the loader revalidation finished).
  React.useEffect(() => {
    if (
      navigation.state === 'loading' &&
      navigation.formAction === navigation.location?.pathname &&
      !action?.validationErrors?.length
    ) {
      preview.regeneratePreview();
    }
  }, [
    action?.validationErrors,
    navigation.formAction,
    navigation.location?.pathname,
    navigation.state,
    preview
  ]);

  // Edit mode: kick off an initial preview render so the iframe is ready
  // when the slideout opens.
  React.useEffect(() => {
    if (props.editMode) {
      preview.regeneratePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every field but the body echoes the stored template, not the form: the form's file ids predate
  // any upload saved since, and the input clears a file whose id it is not sent.
  const storedTemplate = props.initialTemplate;
  const saveTemplateBody = React.useCallback(
    async (content: string) => {
      if (!storedTemplate?.id) return;
      const result = await updateHtmlDocumentTemplateMutation({
        variables: {
          id: storedTemplate.id,
          input: {
            title: storedTemplate.title,
            description: storedTemplate.description ?? '',
            baseStyleId: storedTemplate.baseStyleId || null,
            prefixFileId: storedTemplate.prefixFiles?.[0]?.id ?? null,
            suffixFileId: storedTemplate.suffixFiles?.[0]?.id ?? null,
            bodyTemplate: content
          }
        },
        context: editorSaveContext()
      });
      if (result.errors?.length) {
        throw new Error(result.errors.map((e) => e.message).join(', '));
      }
    },
    [storedTemplate, updateHtmlDocumentTemplateMutation]
  );

  // The workspace drafts from the stored body, which the editor saves apart from the form.
  const bodySave = React.useRef<{
    latest: Promise<void> | null;
    pending: boolean;
    failed: boolean;
  }>({ latest: null, pending: false, failed: false });
  const [bodySaveError, setBodySaveError] = React.useState<string | null>(null);
  const handleEditorContentChange = React.useCallback(
    (content: string) => {
      const save = saveTemplateBody(content);
      bodySave.current = { latest: save, pending: true, failed: bodySave.current.failed };
      const settle = (failed: boolean) => {
        if (bodySave.current.latest === save)
          bodySave.current = { latest: save, pending: false, failed };
      };
      save.then(
        () => settle(false),
        () => settle(true)
      );
      return save;
    },
    [saveTemplateBody]
  );
  // Clicking the button blurs the editor, which sends its last keystrokes; a save already out
  // queues them behind it, so wait until no newer body save has started.
  const bodySettled = async () => {
    for (let seen: Promise<void> | null = null; bodySave.current.latest !== seen; ) {
      seen = bodySave.current.latest;
      await seen?.catch(() => {});
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  };

  const onClose = () => {
    if (props.editMode) {
      navigate(props.listUrl);
      return;
    }
    if (location.key === 'default') {
      navigate(props.listUrl);
    } else {
      navigate(-1);
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append('title', formState.form.title);
    formData.append('description', formState.form.description);
    formData.append('baseStyleId', formState.form.baseStyleId || '');

    formData.append('prefixFileId', prefix.resolveFileId(formState.form.prefixFileId) ?? '');
    formData.append('suffixFileId', suffix.resolveFileId(formState.form.suffixFileId) ?? '');

    const prefixUpload = prefix.getUpload();
    const suffixUpload = suffix.getUpload();
    if (prefixUpload) formData.append('uploadPrefixFile', prefixUpload);
    if (suffixUpload) formData.append('uploadSuffixFile', suffixUpload);

    // Create flow only — body is left untouched on edit; the TipTap editor
    // submits its own mutation when the user types.
    if (!props.editMode && formState.form.bodyTemplate) {
      formData.append('bodyTemplate', formState.form.bodyTemplate);
    }

    appendHeaderFooterToFormData(formState.headerFooter, formData);

    if (formState.templateStyleForm != null) {
      formData.append('style', JSON.stringify(formState.templateStyleForm));
    }

    // The stored prefix/suffix file ids above are sent whatever the choice, so switching an end
    // back to the template's PDF finds it still there.
    if (designsOn) {
      formData.append('designId', designId ?? '');
    }
    // With no design the server reads both ends as TEMPLATE; sending that back would overwrite the
    // choices kept for when a design is chosen again.
    if (designsOn && !withoutDesign) {
      formData.append('startPages', startPages);
      formData.append('endPages', endPages);
      formData.append('printsOwnHeaderFooter', String(printsOwnHeaderFooter));
    }

    if (visibilityEnabled) {
      formData.append('visibility', JSON.stringify(visibilityInputFromValue(formState.visibility)));
    }

    return formData;
  };

  // The form as Save would send it. Use in workspace saves only when this differs from the last
  // save; a staged upload that the reload turns into a stored file id reads as a change, which
  // costs one more save rather than a lost edit.
  const formKey = (data: FormData) =>
    JSON.stringify(
      [...data].map(([name, value]) => [
        name,
        value instanceof File ? [value.name, value.size, value.lastModified] : value
      ])
    );
  const [savedForm, setSavedForm] = React.useState(() => formKey(buildFormData()));
  // Saving moves the navigation off idle and back; a save is done only once it has seen both.
  const [saving, setSaving] = React.useState<{
    phase: 'requested' | 'saving';
    form: string;
    thenOpen: boolean;
  } | null>(null);

  const save = (thenOpen = false) => {
    const data = buildFormData();
    submit(data, {
      method: props.editMode ? 'PUT' : 'POST',
      encType: 'multipart/form-data'
    });
    setSaving({ phase: 'requested', form: formKey(data), thenOpen });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (missingTemplateFile) return;
    save();
  };

  // The workspace drafts from the stored template, so the form's edits are saved before the
  // dialog opens.
  const openUseInWorkspace = () => {
    if (canEdit && bodySave.current.pending) {
      void bodySettled().then(openUseInWorkspace);
      return;
    }
    if (canEdit && bodySave.current.failed) {
      setBodySaveError(
        "The template's text didn't save. Click into the text and out again to retry, then use it in a workspace."
      );
      return;
    }
    setBodySaveError(null);
    if (!canEdit || formKey(buildFormData()) === savedForm) {
      setUseInWorkspaceOpen(true);
      return;
    }
    save(true);
  };
  React.useEffect(() => {
    if (!saving) return;
    if (saving.phase === 'requested') {
      if (navigation.state !== 'idle') setSaving({ ...saving, phase: 'saving' });
      return;
    }
    if (navigation.state !== 'idle') return;
    setSaving(null);
    if (action?.validationErrors?.length || action?.serverError || action?.applicationError) return;
    setSavedForm(saving.form);
    if (saving.thenOpen) setUseInWorkspaceOpen(true);
  }, [action, navigation.state, saving]);

  const titleNoun = props.kind === 'template' ? 'template' : 'style';
  const slideoutTitle = !canEdit
    ? `View ${titleNoun}`
    : props.editMode
      ? `Edit ${titleNoun}`
      : `Create a new ${titleNoun}`;

  return (
    <MountableSlideout
      onClose={onClose}
      title={slideoutTitle}
      size="6.5xl"
      actions={
        canUseInWorkspace && (
          <>
            {bodySaveError && (
              <span role="alert" className="text-sm text-red-600 dark:text-red-400">
                {bodySaveError}
              </span>
            )}
            <Button
              type="button"
              color="sky"
              onClick={openUseInWorkspace}
              disabled={(canEdit && missingTemplateFile) || !!saving?.thenOpen}
            >
              Use in workspace
            </Button>
          </>
        )
      }
    >
      {usableTemplate && (
        <UseTemplateInWorkspaceDialog
          open={useInWorkspaceOpen}
          onClose={() => setUseInWorkspaceOpen(false)}
          templateId={usableTemplate.id}
          templateTitle={usableTemplate.title}
        />
      )}
      {!canEdit && (
        <PermissionNotice className="mb-6">
          You can view this {titleNoun} but not change it.
        </PermissionNotice>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 min-w-0 w-full items-stretch">
        <Form
          onSubmit={handleSubmit}
          className="col-span-1 min-w-0 flex min-h-0 flex-col sm:min-h-[calc(100vh-6rem)]"
          noValidate
        >
          {/* px-0.5 leaves room for FileInput's dashed outline; without it the
              outline gets clipped on the sides by the scroll container. */}
          <Fieldset
            disabled={!canEdit}
            className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-0.5"
          >
            <FieldGroup className="min-w-0">
              <Field>
                <Label>Title</Label>
                <Input
                  className="max-w-xs"
                  value={formState.form?.title}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    formState.updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  invalid={!!formState.getValidationError(formState.validationErrors, ['title'])}
                />
                {!!formState.getValidationError(formState.validationErrors, ['title']) && (
                  <ErrorMessage>
                    {formState.getValidationError(formState.validationErrors, ['title'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={formState.form?.description}
                  rows={2}
                  resizable={false}
                  onChange={(e) => {
                    const description = e.currentTarget.value;
                    formState.updateForm(['description'], (prev) => ({ ...prev, description }));
                  }}
                  invalid={
                    !!formState.getValidationError(formState.validationErrors, ['description'])
                  }
                />
                {!!formState.getValidationError(formState.validationErrors, ['description']) && (
                  <ErrorMessage>
                    {formState.getValidationError(formState.validationErrors, ['description'])}
                  </ErrorMessage>
                )}
              </Field>
              {designsOn ? (
                <HtmlEditorDesignFields
                  offersLegacy={offersLegacy}
                  designId={designId}
                  currentDesign={props.initialTemplate?.design}
                  onDesignChange={setDesignId}
                  printsOwnHeaderFooter={printsOwnHeaderFooter}
                  onPrintsOwnHeaderFooterChange={setPrintsOwnHeaderFooter}
                  startPages={startPages}
                  endPages={endPages}
                  onStartPagesChange={(value) => changePages(setStartPages, prefix, value)}
                  onEndPagesChange={(value) => changePages(setEndPages, suffix, value)}
                  startFileMissing={startFileMissing}
                  endFileMissing={endFileMissing}
                  startUpload={
                    <HtmlEditorPrefixSuffixFiles
                      bare
                      label="Start Pages"
                      noun={titleNoun}
                      existingFiles={props.initialTemplate?.prefixFiles}
                      reset={prefix.reset}
                      onPickFiles={prefix.pickFiles}
                      onResetExisting={prefix.resetExisting}
                    />
                  }
                  endUpload={
                    <HtmlEditorPrefixSuffixFiles
                      bare
                      label="End Pages"
                      noun={titleNoun}
                      existingFiles={props.initialTemplate?.suffixFiles}
                      reset={suffix.reset}
                      onPickFiles={suffix.pickFiles}
                      onResetExisting={suffix.resetExisting}
                    />
                  }
                />
              ) : (
                <>
                  <HtmlEditorPrefixSuffixFiles
                    label="Start Pages"
                    noun={titleNoun}
                    existingFiles={props.initialTemplate?.prefixFiles}
                    reset={prefix.reset}
                    onPickFiles={prefix.pickFiles}
                    onResetExisting={prefix.resetExisting}
                  />
                  <HtmlEditorPrefixSuffixFiles
                    label="End Pages"
                    noun={titleNoun}
                    existingFiles={props.initialTemplate?.suffixFiles}
                    reset={suffix.reset}
                    onPickFiles={suffix.pickFiles}
                    onResetExisting={suffix.resetExisting}
                  />
                  <HtmlEditorHeaderFooterPanel
                    headerFooter={formState.headerFooter}
                    validationErrors={formState.validationErrors}
                    getValidationError={formState.getValidationError}
                    updateHeaderFooterSlot={formState.updateHeaderFooterSlot}
                    setHeaderFooterSlotMode={formState.setHeaderFooterSlotMode}
                    clearValidationErrorForField={formState.clearValidationErrorForField}
                    clearValidationErrorsWithPrefix={formState.clearValidationErrorsWithPrefix}
                  />
                  <Field>
                    <Label>Base Style</Label>
                    <Description>
                      Start from a built-in style; settings below override it.
                    </Description>
                    <Select
                      value={formState.form?.baseStyleId || ''}
                      onChange={(e) => {
                        const baseStyleId = e.currentTarget.value;
                        formState.updateForm(['baseStyleId'], (prev) => ({
                          ...prev,
                          baseStyleId: baseStyleId || null
                        }));
                      }}
                    >
                      <option value="">None</option>
                      <optgroup label="Built-in">
                        <option value="builtin:classic-professional">Classic Professional</option>
                        <option value="builtin:modern-minimalist">Modern Minimalist</option>
                        <option value="builtin:warm-human">Warm Human</option>
                        <option value="builtin:elegant-premium">Elegant Premium</option>
                        <option value="builtin:technical-structured">
                          Technical and Structured
                        </option>
                      </optgroup>
                    </Select>
                  </Field>
                  <HtmlEditorStylePanel
                    value={formState.templateStyleForm}
                    onChange={(updater) => {
                      formState.setTemplateStyleForm((prev) =>
                        typeof updater === 'function'
                          ? (
                              updater as (
                                prev: HtmlDocumentTemplateStyleInput | null
                              ) => HtmlDocumentTemplateStyleInput | null
                            )(prev)
                          : updater
                      );
                    }}
                    hasHeaderFooterImageSlots={hasHeaderFooterImageSlots}
                  />
                </>
              )}
            </FieldGroup>
          </Fieldset>
          <div className="mt-auto flex flex-shrink-0 flex-col items-center gap-3 pt-8 *:w-full sm:flex-row sm:*:w-auto">
            <Button
              type="button"
              color="white"
              disabled={navigation.state === 'submitting'}
              onClick={onClose}
            >
              Close
            </Button>
            {!canEdit ? null : visibilityEnabled ? (
              <VisibilitySplitButton
                type="submit"
                value={formState.visibility}
                onChange={formState.setVisibility}
                disabled={!formState.form || missingTemplateFile}
                loading={navigation.state === 'submitting'}
                matchDisabledAppearance={!formState.form || missingTemplateFile}
              >
                {props.editMode ? `Update ${titleNoun}` : `Create ${titleNoun}`}
              </VisibilitySplitButton>
            ) : (
              <Button
                type="submit"
                color="sky"
                disabled={!formState.form || missingTemplateFile}
                loading={navigation.state === 'submitting'}
              >
                {props.editMode ? `Update ${titleNoun}` : `Create ${titleNoun}`}
              </Button>
            )}
          </div>
        </Form>
        <div className="col-span-1 sm:col-span-2 min-w-0">
          <HtmlEditorPreviewPane
            editMode={props.editMode}
            template={props.initialTemplate}
            isTemplate={isTemplate}
            editable={canEdit}
            renderingPreview={preview.renderingPreview}
            renderPreviewVersion={preview.renderPreviewVersion}
            designId={designsOn ? designId : undefined}
            onEditorContentChange={isTemplate ? handleEditorContentChange : undefined}
            onEditorContentSaved={isTemplate ? preview.bumpPreviewVersion : undefined}
          />
        </div>
      </div>
    </MountableSlideout>
  );
}
